import React, { useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Receipt, TrendingUp, History, Zap, Plus } from 'lucide-react';
import { toast } from 'react-toastify';
import FeeStructureForm from '../components/fees/FeeStructureForm';
import FeeCollectionForm from '../components/fees/FeeCollectionForm';
import GenerateBillForm from '../components/fees/GenerateBillForm';
import AllTransactions from '../components/fees/AllTransactions';
import FeeStructureView from '../components/fees/FeeStructureView';
import { ConfirmDialog } from '../components/ui';
import { feeStructureApi } from '../services/api';
import { FeeStructure, FeeStructureFormData } from '../types';

const tabs = [
  {
    path: 'collect',
    label: 'Collect Fee',
    icon: TrendingUp,
    description: 'Record a fee payment for a student'
  },
  {
    path: 'generate',
    label: 'Generate Bill',
    icon: Zap,
    description: 'Create monthly, admission, annual or custom bills for a student, class or the whole school'
  },
  {
    path: 'transactions',
    label: 'Transactions',
    icon: History,
    description: 'View every fee payment recorded across the school'
  },
  {
    path: 'structures',
    label: 'Fee Structures',
    icon: Receipt,
    description: 'Manage admission, monthly and annual fee structures by class'
  }
];

const Fees: React.FC = () => {
  const location = useLocation();
  const [collectFormKey, setCollectFormKey] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [editingStructure, setEditingStructure] = useState<FeeStructure | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const current = tabs.find(tab => location.pathname.endsWith(`/fees/${tab.path}`)) || tabs[0];
  const Icon = current.icon;

  const handleCreateStructure = async (data: FeeStructureFormData) => {
    try {
      setSubmitting(true);
      await feeStructureApi.create(data);
      setShowForm(false);
      toast.success('Fee structure created successfully');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create fee structure');
      console.error('Error creating fee structure:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStructure = async (data: FeeStructureFormData) => {
    if (!editingStructure) return;

    try {
      setSubmitting(true);
      await feeStructureApi.update(editingStructure.id, data);
      setEditingStructure(null);
      setShowForm(false);
      toast.success('Fee structure updated successfully');
    } catch (error) {
      toast.error('Failed to update fee structure');
      console.error('Error updating fee structure:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteStructure = (structure: FeeStructure) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Fee Structure',
      message: `Are you sure you want to delete "${structure.structureId}"?\n\nThis action cannot be undone and will remove the fee structure permanently.`,
      onConfirm: async () => {
        try {
          await feeStructureApi.delete(structure.id);
          toast.success('Fee structure deleted successfully');
        } catch (error) {
          toast.error('Failed to delete fee structure');
          console.error('Error deleting fee structure:', error);
        }
      }
    });
  };

  const handleEdit = (structure: FeeStructure) => {
    setEditingStructure(structure);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingStructure(null);
  };

  if (showForm) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#f3ede2]">
            {editingStructure ? 'Edit Fee Structure' : 'Create Fee Structure'}
          </h1>
          <p className="text-[#b9ad9b] mt-1">
            {editingStructure ? 'Update fee structure details' : 'Set up a new fee structure for students'}
          </p>
        </div>

        <FeeStructureForm
          onSubmit={editingStructure ? handleEditStructure : handleCreateStructure}
          loading={submitting}
          onCancel={handleCancelForm}
          initialData={editingStructure ? {
            structureId: editingStructure.structureId,
            className: editingStructure.className,
            academicYear: editingStructure.academicYear,
            type: editingStructure.type,
            feeCategories: editingStructure.feeCategories,
            isActive: editingStructure.isActive
          } : undefined}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-[#e2916d] rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#f3ede2]">{current.label}</h1>
            <p className="text-[#b9ad9b] mt-1">{current.description}</p>
          </div>
        </div>
        {current.path === 'structures' && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-[#e2916d] hover:bg-[#f0a67d] text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create Fee Structure</span>
          </button>
        )}
      </div>

      <Routes>
        <Route index element={<Navigate to="collect" replace />} />
        <Route
          path="collect"
          element={
            <FeeCollectionForm
              key={collectFormKey}
              onSuccess={() => {}}
              onCancel={() => setCollectFormKey(k => k + 1)}
            />
          }
        />
        <Route path="generate" element={<GenerateBillForm />} />
        <Route path="transactions" element={<AllTransactions />} />
        <Route path="structures" element={<FeeStructureView onEdit={handleEdit} onDelete={handleDeleteStructure} />} />
        <Route path="*" element={<Navigate to="collect" replace />} />
      </Routes>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type="danger"
      />
    </div>
  );
};

export default Fees;
